const MAX_DECIMAL_PLACES = 10;
const NUMERIC_COMPARISON_TOLERANCE = 1e-10;

type OperatorToken = '+' | '-' | '*' | '/' | '(' | ')';
type Token = number | OperatorToken;

function normalizeExpression(expression: string): string {
  return expression
    .replace(/[$,\s]/g, '')
    .replace(/[xX×]/g, '*')
    .replace(/÷/g, '/')
    .trim();
}

function tokenize(expression: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < expression.length) {
    const char = expression[index];

    if (char === '+' || char === '-' || char === '*' || char === '/' || char === '(' || char === ')') {
      tokens.push(char);
      index += 1;
      continue;
    }

    if (/\d|\./.test(char)) {
      let endIndex = index + 1;
      let hasDecimalPoint = char === '.';

      while (endIndex < expression.length) {
        const nextChar = expression[endIndex];

        if (/\d/.test(nextChar)) {
          endIndex += 1;
          continue;
        }

        if (nextChar === '.' && !hasDecimalPoint) {
          hasDecimalPoint = true;
          endIndex += 1;
          continue;
        }

        break;
      }

      const token = expression.slice(index, endIndex);
      if (token === '.') {
        throw new Error('Invalid number');
      }

      const parsed = Number(token);
      if (!Number.isFinite(parsed)) {
        throw new Error('Invalid number');
      }

      tokens.push(parsed);
      index = endIndex;
      continue;
    }

    throw new Error('Invalid expression');
  }

  return tokens;
}

class ExpressionParser {
  private readonly tokens: Token[];
  private index = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  parse(): number {
    const value = this.parseExpression();

    if (this.index !== this.tokens.length) {
      throw new Error('Unexpected token');
    }

    if (!Number.isFinite(value)) {
      throw new Error('Invalid result');
    }

    return value;
  }

  private parseExpression(): number {
    let value = this.parseTerm();

    while (this.match('+') || this.match('-')) {
      const operator = this.previous();
      const right = this.parseTerm();
      value = operator === '+' ? value + right : value - right;
    }

    return value;
  }

  private parseTerm(): number {
    let value = this.parseFactor();

    while (this.match('*') || this.match('/')) {
      const operator = this.previous();
      const right = this.parseFactor();

      if (operator === '*') {
        value *= right;
      } else {
        value /= right;
      }
    }

    return value;
  }

  private parseFactor(): number {
    if (this.match('+')) {
      return this.parseFactor();
    }

    if (this.match('-')) {
      return -this.parseFactor();
    }

    return this.parsePrimary();
  }

  private parsePrimary(): number {
    if (this.match('(')) {
      const value = this.parseExpression();
      this.consume(')');
      return value;
    }

    const token = this.peek();
    if (typeof token === 'number') {
      this.index += 1;
      return token;
    }

    throw new Error('Expected number');
  }

  private consume(expected: OperatorToken): void {
    if (this.peek() !== expected) {
      throw new Error(`Expected ${expected}`);
    }

    this.index += 1;
  }

  private match(expected: OperatorToken): boolean {
    if (this.peek() !== expected) {
      return false;
    }

    this.index += 1;
    return true;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private previous(): OperatorToken {
    const token = this.tokens[this.index - 1];
    if (typeof token === 'number' || token === undefined) {
      throw new Error('Expected operator');
    }

    return token;
  }
}

export function formatEvaluatedNumber(value: number): string {
  const rounded = Number(value.toFixed(MAX_DECIMAL_PLACES));
  return String(rounded);
}

export function evaluateNumericExpression(expression: string): number | null {
  const normalized = normalizeExpression(expression);
  if (!normalized) {
    return null;
  }

  try {
    const parser = new ExpressionParser(tokenize(normalized));
    const value = parser.parse();
    return Number.isFinite(value) ? Number(value.toFixed(MAX_DECIMAL_PLACES)) : null;
  } catch {
    return null;
  }
}

export function coerceNumericExpressionValue(
  value: number | string | null | undefined
): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Number(value.toFixed(MAX_DECIMAL_PLACES)) : null;
  }

  if (typeof value === 'string') {
    return evaluateNumericExpression(value);
  }

  return null;
}

export function formatNumericInputValue(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return typeof value === 'number' ? formatEvaluatedNumber(value) : value;
}

export function hasEquivalentNumericValue(
  currentValue: number | string | null | undefined,
  nextValue: number
): boolean {
  const currentNumber = coerceNumericExpressionValue(currentValue);
  if (currentNumber === null) {
    return false;
  }

  return Math.abs(currentNumber - nextValue) < NUMERIC_COMPARISON_TOLERANCE;
}
